// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
import { useNavigate } from 'react-router-dom'
import { ExternalLink, ArrowLeft } from 'lucide-react'
import { ModuleShell } from '@/components/ui/ModuleShell'
import { InfoBlock } from '@/components/ui/InfoBlock'
import { ROUTES } from '@/lib/constants/routes'

/**
 * Objectieve, neutrale informatie over euthanasie. Geen advies, geen
 * aanbevelingen. Alle externe links openen buiten WilsApp. WilsApp is geen
 * medische of juridische instantie en faciliteert of beoordeelt niets.
 */
const SOURCES: { label: string; url: string }[] = [
  { label: 'Rijksoverheid - Levenseinde en euthanasie', url: 'https://www.rijksoverheid.nl/onderwerpen/levenseinde-en-euthanasie' },
  { label: 'Rijksoverheid - De 6 zorgvuldigheidseisen',  url: 'https://www.rijksoverheid.nl/onderwerpen/levenseinde-en-euthanasie/zorgvuldigheidseisen' },
  { label: 'KNMG - Euthanasie',                          url: 'https://www.knmg.nl/euthanasie/' },
  { label: 'Expertisecentrum Euthanasie',                url: 'https://expertisecentrumeuthanasie.nl/' },
  { label: 'Thuisarts.nl - Levenseinde',                 url: 'https://www.thuisarts.nl/levenseinde' },
]

export function EuthanasieInfoPage() {
  const navigate = useNavigate()

  return (
    <ModuleShell
      title="Informatie over euthanasie"
      description="Objectieve, algemene informatie. WilsApp geeft geen medisch of juridisch advies en doet geen aanbevelingen. Voor uw eigen situatie bespreekt u dit met uw huisarts."
    >
      <button
        type="button"
        onClick={() => navigate(ROUTES.LEVENSLIJN)}
        className="flex items-center gap-1.5 text-sm text-ink-3 hover:text-ink self-start"
      >
        <ArrowLeft className="h-4 w-4" /> Terug naar Waar staat u nu?
      </button>

      <InfoBlock title="Wat staat er in de wet?" variant="neutral" defaultOpen={true}>
        <p>In Nederland is euthanasie geregeld in de Wet toetsing levensbeëindiging op verzoek en hulp bij zelfdoding (Wtl). Een arts kan in bijzondere gevallen helpen bij het levenseinde, maar alleen als de patiënt daar zelf om vraagt.</p>
        <p>Niemand heeft recht op euthanasie en een arts is nooit verplicht om eraan mee te werken.</p>
      </InfoBlock>

      <InfoBlock title="De zes zorgvuldigheidseisen" variant="neutral" defaultOpen={false}>
        <p>De wet stelt zes eisen. Een arts mag pas meewerken als aan alle eisen is voldaan:</p>
        <p>1. Het verzoek is vrijwillig en goed doordacht (weloverwogen).<br/>
           2. Er is sprake van uitzichtloos en ondraaglijk lijden.<br/>
           3. De patiënt is geïnformeerd over de situatie en de vooruitzichten.<br/>
           4. Er is geen redelijke andere oplossing.<br/>
           5. Ten minste één onafhankelijke arts is geraadpleegd.<br/>
           6. De uitvoering gebeurt medisch zorgvuldig.</p>
      </InfoBlock>

      <InfoBlock title="De rol van de huisarts" variant="neutral" defaultOpen={false}>
        <p>Een euthanasiewens bespreekt u met uw (huis)arts. Het helpt om dit op tijd en regelmatig te doen, zodat uw arts uw situatie en uw wensen goed kent.</p>
        <p>De arts beoordeelt of aan de eisen van de wet wordt voldaan en kan ervoor kiezen niet mee te werken.</p>
      </InfoBlock>

      <InfoBlock title="Schriftelijke euthanasieverklaring" variant="neutral" defaultOpen={false}>
        <p>Een mondeling verzoek is voldoende, maar u kunt uw wens ook opschrijven. Zo'n schriftelijke verklaring kan in de plaats komen van een mondeling verzoek als u dat later zelf niet meer kunt vertellen.</p>
        <p>De arts beoordeelt de situatie ook dan zelf. Bespreek een schriftelijke verklaring daarom met uw arts en uw naasten.</p>
      </InfoBlock>

      <InfoBlock title="Euthanasie bij dementie" variant="neutral" defaultOpen={false}>
        <p>Euthanasie bij dementie is mogelijk, maar het is een complexe en bijzondere situatie. Naarmate dementie vordert, wordt het lastiger om wensen kenbaar te maken en te beoordelen.</p>
        <p>Juist daarom is het belangrijk om wensen tijdig vast te leggen en regelmatig met uw arts te bespreken.</p>
      </InfoBlock>

      <InfoBlock title="Verschil met palliatieve sedatie" variant="neutral" defaultOpen={false}>
        <p>Palliatieve sedatie is het verlichten van lijden in de laatste levensfase, bijvoorbeeld door medicijnen die de patiënt rustiger of slaperig maken. Het is normaal medisch handelen en niet gericht op het beëindigen van het leven.</p>
        <p>Euthanasie is het beëindigen van het leven op verzoek van de patiënt. Dit zijn dus twee verschillende dingen.</p>
      </InfoBlock>

      <InfoBlock title="Verschil euthanasieverklaring en behandelverbod" variant="neutral" defaultOpen={false}>
        <p>Een euthanasieverklaring is een verzoek. Een arts is niet verplicht hieraan mee te werken.</p>
        <p>Een behandelverbod is iets anders: daarmee weigert u een bepaalde behandeling. Een arts moet zo'n weigering in principe volgen.</p>
      </InfoBlock>

      <InfoBlock title="Wat is een SCEN-arts?" variant="neutral" defaultOpen={false}>
        <p>SCEN staat voor Steun en Consultatie bij Euthanasie in Nederland. Een SCEN-arts is een onafhankelijke arts die de behandelend arts kan raadplegen om mee te kijken bij een euthanasieverzoek.</p>
      </InfoBlock>

      <div className="rounded-xl border border-sand-300 bg-sand px-4 py-3 flex flex-col gap-2">
        <p className="text-sm font-semibold text-ink">Meer lezen bij onafhankelijke bronnen</p>
        <p className="text-xs text-ink-4">Deze links openen buiten WilsApp.</p>
        <div className="flex flex-col gap-1.5 mt-1">
          {SOURCES.map(s => (
            <a
              key={s.url}
              href={s.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-sm text-sage-700 hover:text-sage-800 hover:underline"
            >
              <ExternalLink className="h-3.5 w-3.5 flex-shrink-0" />
              <span>{s.label}</span>
            </a>
          ))}
        </div>
      </div>

      <p className="text-xs text-ink-4 leading-relaxed">
        WilsApp is geen medische of juridische instantie. Deze pagina geeft alleen algemene informatie en
        geen advies. Voor vragen over uw eigen situatie bespreekt u dit met uw huisarts of een andere
        bevoegde professional.
      </p>
    </ModuleShell>
  )
}
